/**
 * Shopping-list aggregator for meal plans.
 *
 * Each `food_item` field follows the convention produced by `parse-meal-plan`
 * and the manual editor:
 *
 *   <recipe name>
 *   * <quantity> <unit> de <ingredient>
 *   * <quantity> <unit> de <ingredient>
 *   ...
 *
 * Quantities can be:
 *   - integers          → "2 piezas de Pan"
 *   - decimals          → "30.0 gramos de Queso"
 *   - simple fractions  → "1/2 pieza de Aguacate"
 *   - mixed numbers     → "1 1/2 pieza de Tortilla"
 *
 * We aggregate the same ingredient ONLY when the unit matches (literal text
 * lowercased). Mixing "tazas" + "gramos" would lie, so they list separately.
 *
 * Output is grouped by ingredient, sorted alphabetically. Units inside an
 * ingredient are sorted by total quantity desc.
 */

export interface ParsedIngredient {
  qty: number;
  unit: string;       // canonical lowercase, e.g. "pieza", "gramos"
  unitLabel: string;  // first seen verbatim form, e.g. "Pieza"
  ingredient: string; // verbatim, capitalization preserved
}

export interface AggregatedIngredient {
  ingredient: string;
  // One row per distinct unit (since you can't sum "tazas" with "gramos")
  units: { qty: number; unit: string }[];
}

/** Parse a Spanish numeric token: "30.0" | "1/2" | "1 1/2" → number. */
export function parseQuantityToken(token: string): number | null {
  const t = token.trim();
  if (!t) return null;

  // Mixed: "1 1/2"
  const mixed = t.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const num = Number(mixed[2]);
    const den = Number(mixed[3]);
    if (den !== 0) return whole + num / den;
    return null;
  }

  // Simple fraction: "1/2"
  const frac = t.match(/^(\d+)\/(\d+)$/);
  if (frac) {
    const num = Number(frac[1]);
    const den = Number(frac[2]);
    if (den !== 0) return num / den;
    return null;
  }

  // Decimal/integer
  const num = Number(t.replace(',', '.'));
  return Number.isFinite(num) ? num : null;
}

/**
 * Parse a single bullet line of the form:
 *   "* 1 1/2 pieza de Tortilla de harina"
 *
 * The "de" before the ingredient is the separator. Some units are multi-word
 * ("rebanadas delgadas") so we greedily match qty first, then take everything
 * up to "de " as the unit, and the rest as the ingredient.
 *
 * Returns null if the line doesn't match the bullet pattern (e.g. it's a
 * recipe title or empty).
 */
export function parseBulletLine(rawLine: string): ParsedIngredient | null {
  const line = rawLine.trim();
  if (!line.startsWith('*')) return null;

  // strip leading "* "
  const body = line.replace(/^\*\s*/, '').trim();
  if (!body) return null;

  // Quantity: leading number / fraction / mixed (greedy until non-qty char)
  // Examples to match: "1", "30.0", "1/2", "1 1/2", "1 1/6"
  const qtyMatch = body.match(/^(\d+(?:[.,]\d+)?(?:\s+\d+\/\d+)?|\d+\/\d+)\s+/);
  if (!qtyMatch) return null;

  const qty = parseQuantityToken(qtyMatch[1]);
  if (qty === null) return null;

  const rest = body.slice(qtyMatch[0].length);

  // Split "<unit> de <ingredient>". The first " de " is the boundary.
  // (some ingredient names contain " de " too, e.g. "Tortilla de harina",
  //  so we only split at the FIRST occurrence.)
  const deIdx = rest.toLowerCase().indexOf(' de ');
  if (deIdx === -1) return null;

  const unitLabel = rest.slice(0, deIdx).trim();
  const ingredient = rest.slice(deIdx + 4).trim();

  if (!unitLabel || !ingredient) return null;

  return {
    qty,
    unit: unitLabel.toLowerCase(),
    unitLabel,
    ingredient,
  };
}

/**
 * Known Spanish measurement units, lowercased. Used by the free-text
 * parser to know when the second token after the quantity is the unit
 * vs. part of the ingredient name.
 *
 * Example: "1 cdta de aceite de oliva"
 *           ^   ^^^^                      ← qty + unit
 *               cdta is in the list → ingredient = "aceite de oliva"
 *
 * Example: "2 huevos completos"
 *           ^   ^^^^^^                    ← qty + ingredient (no unit)
 *               huevos NOT in the list → ingredient = "huevos completos"
 */
const KNOWN_UNITS = new Set([
  // weight
  'g', 'gr', 'gramo', 'gramos', 'kg', 'kilo', 'kilos',
  // volume
  'ml', 'l', 'litro', 'litros',
  // imperial
  'oz', 'lb', 'lbs',
  // spoon/cup
  'cda', 'cdas', 'cucharada', 'cucharadas',
  'cdta', 'cdtas', 'cucharadita', 'cucharaditas',
  'tza', 'tzas', 'taza', 'tazas', 'vaso', 'vasos',
  // count
  'pieza', 'piezas', 'pza', 'pzas',
  'rebanada', 'rebanadas', 'reb',
  'lata', 'latas',
  'hoja', 'hojas',
  'porcion', 'porciones', 'porción',
  'tira', 'tiras',
  'puñado', 'punado',
]);

/**
 * Parse a free-text ingredient phrase like "140 g pechuga" or
 * "1/2 tza arroz hervido" or "10 uvas verdes".
 *
 * Returns null for things with no quantity at all ("ensalada al gusto"),
 * since those can't be aggregated honestly. The dialog handles those
 * separately (see `parseFreeTextSegments`).
 */
function parseFreeTextSegment(rawSegment: string): ParsedIngredient | null {
  const seg = rawSegment.trim();
  if (!seg) return null;

  // Quantity: leading number / fraction / mixed
  const qtyMatch = seg.match(/^(\d+(?:[.,]\d+)?(?:\s+\d+\/\d+)?|\d+\/\d+)\s+/);
  if (!qtyMatch) return null;

  const qty = parseQuantityToken(qtyMatch[1]);
  if (qty === null) return null;

  let rest = seg.slice(qtyMatch[0].length).trim();
  if (!rest) return null;

  // Strip an optional leading "de " (some lines write "1 cdta de aceite",
  // others "1 cdta aceite" — both should normalise the same way).
  rest = rest.replace(/^de\s+/i, '');

  // First word is the unit candidate. Compare lowercase, strip trailing
  // punctuation. If it's a known unit, the rest is the ingredient. If
  // not, treat the whole rest as the ingredient with an empty unit.
  const firstSpace = rest.indexOf(' ');
  const firstWord = (firstSpace === -1 ? rest : rest.slice(0, firstSpace))
    .toLowerCase()
    .replace(/[.,;]+$/, '');
  const isKnownUnit = KNOWN_UNITS.has(firstWord);

  let unitLabel = '';
  let ingredient = rest;
  if (isKnownUnit && firstSpace !== -1) {
    unitLabel = rest.slice(0, firstSpace).replace(/[.,;]+$/, '');
    ingredient = rest.slice(firstSpace + 1).replace(/^de\s+/i, '').trim();
  }

  // strip trailing periods / commas from ingredient
  ingredient = ingredient.replace(/[.,;]+$/, '').trim();
  if (!ingredient) return null;

  // Capitalise first letter for display
  const display = ingredient.charAt(0).toUpperCase() + ingredient.slice(1);

  return {
    qty,
    unit: unitLabel.toLowerCase() || '',
    unitLabel: unitLabel || 'pza',
    ingredient: display,
  };
}

/**
 * Free-text food_item: one or several lines of comma-separated phrases,
 * e.g. "2 huevos completos, 2 claras, 1 cdta de aceite de oliva.".
 *
 * We split on commas, periods (when not inside a number) and " y "
 * connectors. Each segment goes through `parseFreeTextSegment`. Segments
 * that fail (no quantity) are returned as raw strings so the caller can
 * surface them in a separate "al gusto" bucket.
 */
function parseFreeTextSegments(text: string): {
  withQty: ParsedIngredient[];
  withoutQty: string[];
} {
  // Proteger comas y conectores " y " que viven DENTRO de paréntesis.
  // Sin esto, "500g de Verduras (brócoli, espinaca o calabaza)" se
  // partía en 3 segmentos huérfanos con paréntesis abiertos/cerrados
  // sueltos. Reemplazamos por un placeholder y lo restauramos al final.
  const COMMA = '';
  const Y_CONN = '';
  const protectedText = text.replace(/\(([^()]*)\)/g, (_m, inner) => {
    const safe = inner
      .replace(/,/g, COMMA)
      .replace(/\s+y\s+/gi, Y_CONN);
    return '(' + safe + ')';
  });
  const segments = protectedText
    .replace(/\s+y\s+/gi, ', ')
    .split(/(?<!\d)[.](?!\d)|,/)
    .map(s =>
      s
        .trim()
        .replace(new RegExp(COMMA, 'g'), ',')
        .replace(new RegExp(Y_CONN, 'g'), ' y '),
    )
    .filter(Boolean);

  const withQty: ParsedIngredient[] = [];
  const withoutQty: string[] = [];

  for (const seg of segments) {
    const parsed = parseFreeTextSegment(seg);
    if (parsed) {
      withQty.push(parsed);
    } else {
      // Drop trailing punctuation, capitalise, store as-is so it can
      // appear in the "al gusto" section of the dialog.
      const clean = seg.replace(/[.,;]+$/, '').trim();
      if (clean) {
        withoutQty.push(clean.charAt(0).toUpperCase() + clean.slice(1));
      }
    }
  }

  return { withQty, withoutQty };
}

/**
 * Parse a `food_item` text block. Auto-detects format:
 *
 * - If any line starts with "*", we treat it as the **bullet format**
 *   produced by parse-meal-plan (Gemini) and only consider bullet lines.
 *   The first non-bullet line is the recipe name and is skipped.
 *
 * - Otherwise we treat the whole text as **free-text format** typed by
 *   the coach, comma-separated ingredients (e.g. "2 huevos, 1 tortilla").
 *
 * Returns both the quantified ingredients (summable) and the free-text
 * "al gusto" entries that have no quantity attached.
 */
export function parseFoodItem(foodItem: string): {
  withQty: ParsedIngredient[];
  withoutQty: string[];
} {
  const lines = foodItem.split('\n');
  const hasBullets = lines.some(l => l.trim().startsWith('*'));

  if (hasBullets) {
    const withQty = lines
      .map(parseBulletLine)
      .filter((x): x is ParsedIngredient => x !== null);
    return { withQty, withoutQty: [] };
  }

  // Free-text mode: parse every line as comma-separated segments.
  const withQty: ParsedIngredient[] = [];
  const withoutQty: string[] = [];
  for (const line of lines) {
    const r = parseFreeTextSegments(line);
    withQty.push(...r.withQty);
    withoutQty.push(...r.withoutQty);
  }
  return { withQty, withoutQty };
}

/**
 * Aggregate ingredients across many `food_item` strings.
 *
 * @param foodItems   raw `food_item` strings from `client_meal_plans`
 * @param dayCount    multiply quantities by this. Use 1 for "show me one
 *                    week as planned" (each row already represents one day);
 *                    use a different number if you want a multi-week run.
 */
export interface ShoppingList {
  /** Items with quantities, summed across all meals. */
  quantified: AggregatedIngredient[];
  /** "Al gusto" / unquantified ingredients (deduplicated). */
  unquantified: string[];
}

/**
 * Aggregate ingredients across many `food_item` strings. Returns ONLY
 * the quantified ones (back-compat). Use `buildShoppingList` to also
 * get the "al gusto" bucket.
 */
export function aggregateShoppingList(
  foodItems: string[],
  dayCount = 1,
): AggregatedIngredient[] {
  return buildShoppingList(foodItems, dayCount).quantified;
}

/**
 * Cada comida del plan: el `food_item` y el día de la semana al que aplica.
 * Si `day_of_week` es null/undefined la comida aplica todos los días (×7);
 * si tiene un valor concreto ("Lunes") aplica solo ese día (×1).
 *
 * Esto es lo que produce el cálculo correcto de "lista para una semana":
 * un huevo del lunes cuenta 1 vez, pero el café "todos los días" cuenta 7.
 */
export interface MealForShopping {
  food_item: string;
  day_of_week?: string | null;
}

/**
 * Versión que pesa cada comida según los días que aplica para producir
 * la lista de compras de UNA semana completa. Recomendado sobre
 * `buildShoppingList(strings[])` porque toma en cuenta `day_of_week`.
 */
export function buildShoppingListForWeek(meals: MealForShopping[]): ShoppingList {
  // Internamente reusa buildShoppingList llamándolo por grupos de mismo
  // dayCount: las comidas "todos los días" se acumulan con dayCount=7,
  // las de un día específico con dayCount=1. Concatenar resultados no
  // funciona porque buildShoppingList agrega; en su lugar inline-amos
  // la agregación con dayCount per-meal.
  const map = new Map<
    string,
    {
      displayName: string;
      units: Map<string, { qty: number; unitLabel: string }>;
    }
  >();
  const unquantSet = new Map<string, string>();

  for (const meal of meals) {
    const dayCount = meal.day_of_week == null || meal.day_of_week.trim() === '' ? 7 : 1;
    const { withQty, withoutQty } = parseFoodItem(meal.food_item || '');
    for (const u of withoutQty) {
      const key = u.toLowerCase();
      if (!unquantSet.has(key)) unquantSet.set(key, u);
    }
    for (const p of withQty) {
      const key = p.ingredient.toLowerCase();
      let entry = map.get(key);
      if (!entry) {
        entry = { displayName: p.ingredient, units: new Map() };
        map.set(key, entry);
      }
      const u = entry.units.get(p.unit);
      if (u) {
        u.qty += p.qty * dayCount;
      } else {
        entry.units.set(p.unit, { qty: p.qty * dayCount, unitLabel: p.unitLabel });
      }
    }
  }

  const quantified = Array.from(map.values())
    .map(({ displayName, units }) => ({
      ingredient: displayName,
      units: Array.from(units.values())
        .map(u => ({ qty: roundQty(u.qty), unit: u.unitLabel }))
        .sort((a, b) => b.qty - a.qty),
    }))
    .sort((a, b) =>
      a.ingredient.localeCompare(b.ingredient, 'es', { sensitivity: 'base' }),
    );

  const unquantified = Array.from(unquantSet.values()).sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' }),
  );

  return { quantified, unquantified };
}

export function buildShoppingList(
  foodItems: string[],
  dayCount = 1,
): ShoppingList {
  // ingredient (lowercased) → { displayName, units: Map<unit, {qty, label}> }
  const map = new Map<
    string,
    {
      displayName: string;
      units: Map<string, { qty: number; unitLabel: string }>;
    }
  >();
  const unquantSet = new Map<string, string>(); // lower → display

  for (const food of foodItems) {
    const { withQty, withoutQty } = parseFoodItem(food);
    for (const u of withoutQty) {
      const key = u.toLowerCase();
      if (!unquantSet.has(key)) unquantSet.set(key, u);
    }
    for (const p of withQty) {
      const key = p.ingredient.toLowerCase();
      let entry = map.get(key);
      if (!entry) {
        entry = { displayName: p.ingredient, units: new Map() };
        map.set(key, entry);
      }
      const u = entry.units.get(p.unit);
      if (u) {
        u.qty += p.qty * dayCount;
      } else {
        entry.units.set(p.unit, {
          qty: p.qty * dayCount,
          unitLabel: p.unitLabel,
        });
      }
    }
  }

  const quantified = Array.from(map.values())
    .map(({ displayName, units }) => ({
      ingredient: displayName,
      units: Array.from(units.values())
        .map(u => ({ qty: roundQty(u.qty), unit: u.unitLabel }))
        .sort((a, b) => b.qty - a.qty),
    }))
    .sort((a, b) =>
      a.ingredient.localeCompare(b.ingredient, 'es', { sensitivity: 'base' }),
    );

  const unquantified = Array.from(unquantSet.values()).sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' }),
  );

  return { quantified, unquantified };
}

/** Round to 2 decimals, drop trailing zeros so "30.00" → "30". */
function roundQty(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Format a single line for clipboard/WhatsApp output. */
export function formatLine(item: AggregatedIngredient): string {
  const qtys = item.units
    .map(u => `${formatQty(u.qty)} ${u.unit}`)
    .join(' + ');
  return `• ${item.ingredient} — ${qtys}`;
}

export function formatQty(n: number): string {
  // Show fraction-friendly numbers when possible (½, ¼, ¾)
  const whole = Math.floor(n);
  const frac = n - whole;
  if (Math.abs(frac) < 0.01) return String(whole);
  if (Math.abs(frac - 0.5) < 0.01) return whole === 0 ? '½' : `${whole}½`;
  if (Math.abs(frac - 0.25) < 0.01) return whole === 0 ? '¼' : `${whole}¼`;
  if (Math.abs(frac - 0.75) < 0.01) return whole === 0 ? '¾' : `${whole}¾`;
  if (Math.abs(frac - 0.33) < 0.02) return whole === 0 ? '⅓' : `${whole}⅓`;
  if (Math.abs(frac - 0.67) < 0.02) return whole === 0 ? '⅔' : `${whole}⅔`;
  return n.toFixed(2).replace(/\.?0+$/, '');
}
